/**
 * COMPONENTE QUICK ACCESS SECTION
 * ================================
 * Muestra las tarjetas de acceso rápido a las secciones principales
 */

import React, { useState, useEffect } from 'react';
import { FileText, Calendar, DollarSign, Gavel } from 'lucide-react';
import { Card } from '../ui/card';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const QuickAccessSection = () => {
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.2 });
  const [proximoRemate, setProximoRemate] = useState(null);
  const navigate = useNavigate();

  // Cargar el próximo remate para obtener su número
  useEffect(() => {
    const fetchProximoRemate = async () => {
      try {
        const data = await api.getProximoRemate();
        setProximoRemate(data);
      } catch (error) {
        console.error('Error loading proximo remate:', error);
      }
    };

    fetchProximoRemate();
  }, []);

  // Definición de los accesos rápidos
  const quickLinks = [
    {
      icon: FileText,
      label: 'Próximo Remate',
      path: proximoRemate ? `/lotes?remate=${proximoRemate.numero}` : '/lotes',
      enabled: true
    },
    {
      icon: Calendar,
      label: 'Calendario',
      path: '/calendario',
      enabled: true
    },
    {
      icon: Gavel,
      label: 'Remates',
      path: '/remates',
      enabled: true
    },
    {
      icon: DollarSign,
      label: 'Cotizaciones',
      path: '/cotizaciones',
      enabled: false
    }
  ];

  const handleClick = (link) => {
    if (link.enabled && link.path) {
      navigate(link.path);
    }
  };

  return (
    <section ref={sectionRef} className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Efectos de fondo animados */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-green-100 rounded-full blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-100 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className={`text-center mb-8 md:mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-4">Accesos Rápidos</h2>
          <p className="text-gray-600 text-base md:text-lg">Accede rápidamente a la información que necesitas</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {quickLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <Card
                key={index}
                onClick={() => handleClick(link)}
                className={`group border-0 shadow-lg transition-all duration-500 transform ${link.enabled
                    ? 'cursor-pointer hover:shadow-xl hover:scale-105 hover:-translate-y-2'
                    : 'cursor-not-allowed opacity-50'
                  } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                style={{
                  transitionDelay: isVisible ? `${index * 0.1}s` : '0s'
                }}
              >
                <div className="p-4 md:p-6 flex flex-col items-center text-center space-y-2 md:space-y-4">
                  <div className={`relative w-12 h-12 md:w-16 md:h-16 rounded-2xl flex items-center justify-center transform transition-all duration-300 shadow-lg ${link.enabled
                      ? 'bg-[#2d6c3a] group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-green-500/30'
                      : 'bg-gray-400'
                    }`}>
                    <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    {link.enabled && (
                      <div className="absolute inset-0 bg-white rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                    )}
                  </div>
                  <h3 className={`text-xs md:text-sm font-bold transition-colors ${link.enabled
                      ? 'text-gray-900 group-hover:text-green-600'
                      : 'text-gray-400'
                    }`}>
                    {link.label}
                  </h3>
                  {!link.enabled && (
                    <span className="text-xs text-gray-400">Próximamente</span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default QuickAccessSection;